import { Text, StyleSheet, type TextProps, Platform } from "react-native";
import type { FontVariant } from "react-native";

import { useTheme } from "@/hooks/useTheme";
import { TypographyV3, AppFonts, Tracking } from "@/constants/theme";

export type ThemedTextProps = TextProps & {
  lightColor?: string;
  darkColor?: string;
  type?: "display" | "h1" | "h2" | "h3" | "h4" | "body" | "small" | "caption" | "link" | "tabular"
    | "sectionLabel" | "micro" | "badge";
};

export function ThemedText({
  style,
  lightColor,
  darkColor,
  type = "body",
  ...rest
}: ThemedTextProps) {
  const { theme, isDark } = useTheme();

  const getColor = () => {
    if (isDark && darkColor) return darkColor;
    if (!isDark && lightColor) return lightColor;
    if (type === "link") return theme.goldText;
    if (["body", "small"].includes(type)) return theme.textSecondary;
    if (["caption", "sectionLabel", "micro"].includes(type)) return theme.textTertiary;
    return theme.textPrimary;
  };

  const getTypeStyle = () => {
    const isWeb = Platform.OS === "web";
    // Inter is the product voice. Fraunces is reserved for the explicit
    // `display` verdict role, never inherited by normal H1/H2 headings.
    const font = isWeb ? AppFonts.sans : undefined;
    const verdict = isWeb ? AppFonts.serif : "Fraunces_400Regular";
    const mono = isWeb ? AppFonts.mono : undefined;
    switch (type) {
      case "display":
        return {
          ...TypographyV3.display,
          fontFamily: verdict,
          fontWeight: "400" as const,
          letterSpacing: -0.02 * TypographyV3.display.fontSize,
        };
      case "h1":
        return { ...TypographyV3.h1, fontFamily: font, fontWeight: "700" as const };
      case "h2":
        return { ...TypographyV3.h2, fontFamily: font, fontWeight: "600" as const };
      case "h3":
        // h3 and below return to the sans voice — serif at 18px in a dense
        // dashboard reads as a typo rather than as authority.
        return { ...TypographyV3.h3, fontFamily: font };
      case "h4":
        return { ...TypographyV3.h3, fontSize: 20, lineHeight: 28, fontFamily: font };
      case "body":
        return { ...TypographyV3.body, fontFamily: font };
      case "small":
        return { ...TypographyV3.small, fontFamily: font };
      case "caption":
        return { ...TypographyV3.caption, fontFamily: font };
      case "link":
        // goldText, not primary: Yield Gold is 2.3:1 on Warm Sand and would
        // fail WCAG-AA as a link. See the contrast contract in theme.ts.
        return { ...TypographyV3.bodyMedium, color: theme.goldText, fontFamily: font };
      case "tabular":
        return { ...TypographyV3.price, fontFamily: mono, fontVariant: ["tabular-nums" as FontVariant] };
      case "sectionLabel":
        // The tactical dashboard label: small, uppercase, widely tracked.
        // letterSpacing is POINTS in RN, so 0.2em at 12px is 2.4 — not 0.2.
        return {
          fontSize: 12,
          lineHeight: 16,
          fontWeight: "600" as const,
          letterSpacing: Tracking.tactical(12),
          textTransform: "uppercase" as const,
          fontFamily: font,
        };
      case "micro":
        // DS 12px floor (theme.ts Principle 02) — nothing whispers.
        return {
          fontSize: 12,
          lineHeight: 16,
          fontWeight: "700" as const,
          letterSpacing: Tracking.wide(12),
          fontFamily: font,
        };
      case "badge":
        return {
          fontSize: 12,
          lineHeight: 16,
          fontWeight: "700" as const,
          letterSpacing: Tracking.wide(12),
          fontFamily: font,
        };
      default:
        return { ...TypographyV3.body, fontFamily: font };
    }
  };

  // Every `type` ships a matched fontSize/lineHeight pair. Call sites very
  // often override ONLY fontSize — `type="body"` (lineHeight 25) with
  // `fontSize: 36` for a display headline is the common one — which leaves the
  // line box smaller than the glyphs. On one line that clips descenders; on
  // two it makes them collide, which is exactly how the Portfolio empty state
  // shipped with "Nothing here yet —" overlapping "connect a portfolio".
  //
  // Rather than hunt every call site (and re-hunt after every new one), derive
  // a proportional line height whenever fontSize is overridden without it.
  // 1.12 is tight enough for a display serif and loose enough to clear
  // descenders; an explicit lineHeight always wins.
  const typeStyle = getTypeStyle() as { fontSize?: number; lineHeight?: number };
  const flat = StyleSheet.flatten(style) as { fontSize?: number; lineHeight?: number } | undefined;
  const needsLineHeight =
    flat?.fontSize != null &&
    flat.lineHeight == null &&
    flat.fontSize > (typeStyle.lineHeight ?? 0) * 0.92;
  const derived = needsLineHeight
    ? { lineHeight: Math.round(flat!.fontSize! * 1.12) }
    : null;

  return (
    <Text style={[{ color: getColor() }, typeStyle, style, derived]} {...rest} />
  );
}
